import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

const MODEL_URL = '/cessna_180_skywagon.glb';
const GROUND_URL = '/river-birdseye.jpg';
const CLOUDS_URL = '/clouds.png';
const SUNSET_URL = '/sunset_original.jpg';


class PlaneScene {
  constructor(model) {
    this.contentMax = 1200;
    this.baseWidth = Math.min(window.innerWidth, this.contentMax);
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setSize(this.baseWidth, window.innerHeight);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.setPixelRatio(window.devicePixelRatio);

    this.scene = new THREE.Scene();

    this.camera = new THREE.PerspectiveCamera(45, this.baseWidth / window.innerHeight, 1, 2000);
    this.camera.position.set(0, 0, 180);
    this.camera.lookAt(new THREE.Vector3(0, 25, 0));

    this.light = new THREE.PointLight(0xffffff, 0.75);
    this.light.position.set(70, -20, 150);
    this.scene.add(this.light);

    this.softLight = new THREE.AmbientLight(0xffffff, 1.5);
    this.scene.add(this.softLight);

    this.modelGroup = new THREE.Group();
    this.modelGroup.add(model);
    this.scene.add(this.modelGroup);

    this.w = this.baseWidth;
    this.h = window.innerHeight;
    this.onResize();
    window.addEventListener('resize', this.onResize);
  }

  render = () => {
    this.camera.aspect = this.w / this.h;
    this.renderer.render(this.scene, this.camera);
  };

  onResize = () => {
    this.w = Math.min(window.innerWidth, this.contentMax);
    this.h = window.innerHeight;

    this.camera.aspect = this.w / this.h;
    const camZ = (this.baseWidth - this.w) / 3;
    this.camera.position.z = camZ < 180 ? 180 : camZ;
    this.camera.updateProjectionMatrix();

    this.renderer.setSize(this.w, this.h);
    this.render();
  };

  dispose() {
    window.removeEventListener('resize', this.onResize);
    this.scene.traverse(child => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) {
        if (Array.isArray(child.material)) child.material.forEach(m => m.dispose());
        else child.material.dispose();
      }
    });
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }
}

export function AirplaneAnimation() {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);

  useEffect(() => {
    let disposed = false;
    let scene = null;

    const loader = new GLTFLoader();
    loader.load(MODEL_URL, (gltf) => {
      if (disposed) return;

      const model = gltf.scene;

      const box = new THREE.Box3().setFromObject(model);
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const contentW = Math.min(window.innerWidth, 1200);
      const baseSize = 40 * contentW / 1200;
      const scale = baseSize / maxDim;
      model.scale.setScalar(scale);

      const center = box.getCenter(new THREE.Vector3());
      model.position.set(-center.x * scale, -center.y * scale, -center.z * scale);

      model.traverse((child) => {
        if (child.isMesh) {
          child.material = new THREE.MeshPhongMaterial({
            color: 0x2B2520,
            specular: 0xF7F4EF,
            shininess: 5,
            flatShading: true,
          });
        }
      });

      scene = new PlaneScene(model);
      sceneRef.current = scene;
      containerRef.current.appendChild(scene.renderer.domElement);

      setupAnimation(scene);
    }, undefined, () => {
      if (disposed) return;
      const spinner = containerRef.current?.querySelector('.airplane-loading');
      if (spinner) spinner.style.display = 'none';
    });

    return () => {
      disposed = true;
      ScrollTrigger.getAll().forEach(t => t.kill());
      if (scene) scene.dispose();
    };
  }, []);

  return (
    <div ref={containerRef} className="airplane-content">
      <div className="airplane-loading"></div>
      <div className="airplane-trigger"></div>

      {/* HERO */}
      <div className="airplane-section hero">
        <div className="hero-text">
          <div className="hero-eyebrow">
            <span className="chip chip--on-dark">Free during beta</span>
            <span className="chip chip--on-dark">Built by a pilot</span>
          </div>
          <h1>Build your <span className="hero-accent">kneeboard</span> for the way you fly</h1>
          <p>Create, share, and copy tabbed checklists and reference pages for any aircraft. Export-ready PDFs in minutes.</p>
          <div>
            <a className="btn btn--primary btn--xl airplane-hangar-mt"
               href="/create-account"
               onClick={() => window.track && window.track('create_account_clicked', { location: 'hero' })}>
              Create account
            </a>
          </div>
          <a className="btn btn--tertiary airplane-hangar-mt"
             href="/flytabs-sample.pdf"
             download="FlyTabs-sample.pdf"
             onClick={() => window.track && window.track('sample_pdf_download', { location: 'hero' })}>
            Or try in your ForeFlight — download sample PDF →
          </a>
        </div>
        <div className="airplane-scroll-cta">Scroll</div>
      </div>

      {/* PREFLIGHT (text left) */}
      <div className="airplane-section">
        <h2>Preflight complete.</h2>
        <p>Walk around the aircraft with every check in front of you — interior switches, exterior inspection, securing the aircraft.</p>
        <img className="doc-screenshot" src="/Landing-preflight.png" alt="Preflight Inspection page with tabbed checklist" />
      </div>

      {/* TAKEOFF + CLIMB */}
      <div className="airplane-ground-container">
        <div className="airplane-parallax airplane-ground" style={{backgroundImage: `url(${GROUND_URL})`}}></div>
        <div className="airplane-parallax airplane-clouds-overlay" style={{backgroundImage: `url(${CLOUDS_URL})`}}></div>
        <div className="airplane-section right">
          <h2>Cleared for takeoff.</h2>
          <p>Normal takeoff, memory items, after-takeoff checks — one glance at your lap and you're set.</p>
          <img className="doc-screenshot" src="/Landing-takeoff.png" alt="Engine Start & Departure checklist" />
        </div>
        <div className="airplane-section right">
          <h2>Passing through flight levels.</h2>
          <p>Cruise checklists, leaning procedures, VOR idents, and VFR routes. Keep your in-flight reference pages tabbed and ready.</p>
          <img className="doc-screenshot" src="/Landing-final.png" alt="Cruise & Enroute checklist" />
        </div>
        <div className="airplane-section short">
          <h2>Emergencies? Covered.</h2>
          <p>Forced landings, precautionary approaches, ditching procedures. The tabs you hope you never need — but always have.</p>
          <img className="doc-screenshot" src="/Landing-emerg.png" alt="Emergency Procedures checklist" />
        </div>
        {/* SHUTDOWN (text right) */}
        <div className="airplane-section right">
          <h2>Shut it down.</h2>
          <p>After-landing checks, shutdown flows, and tie-down steps — all right there when you clear the runway. From first walkaround to last switch, every phase has its own tab.</p>
          <img className="doc-screenshot" src="/Landing-shutdown.png" alt="After Landing & Shutdown checklist" />
        </div>
        <div className="airplane-parallax airplane-clouds" style={{backgroundImage: `url(${CLOUDS_URL})`}}></div>
      </div>

      {/* HANGAR (text left, tablet right) */}
      <div className="airplane-section short hangar">
        <div className="hangar-layout">
          <div className="hangar-text">
            <h2>Shared by pilots, ready to fly.</h2>
            <p>Browse documents shared by other pilots — checklists, reference pages, performance cards, and more. Find your aircraft, clone what you need, and make it yours.</p>
            <a className="btn btn--primary btn--xl airplane-hangar-mt" href="/library">Explore the library</a>
          </div>
        </div>
      </div>

      {/* SUNSET */}
      <div className="airplane-sunset">
        <div className="airplane-sunset-bg" style={{backgroundImage: `url(${SUNSET_URL})`}}></div>
        <div className="airplane-section end">
          <h2>File your flight plan.</h2>
          <p className="airplane-sunset-sub">Create your account and be first in the pattern when FlyTabs launches.</p>
          <a className="btn btn--primary btn--xl airplane-hangar-mt"
             href="/create-account"
             onClick={() => window.track && window.track('create_account_clicked', { location: 'sunset_cta' })}>
            Create account
          </a>
        </div>
      </div>
    </div>
  );
}

function setupAnimation(scene) {
  const plane = scene.modelGroup;

  gsap.set('canvas', { x: '30%', autoAlpha: 0 });
  gsap.to('.airplane-loading', { autoAlpha: 0 });
  gsap.to('.airplane-scroll-cta', { opacity: 1 });

  const tau = Math.PI * 2;

  // This GLB model's nose points +z. The original OBJ's nose pointed -z.
  // Offset all y-rotations by 0.5*tau (180°) to flip the model.
  const yOff = tau * 0.25;

  const contentW = Math.min(window.innerWidth, 1200);
  const halfAngle = (45 / 2) * Math.PI / 180;
  const visibleHalfW = 180 * Math.tan(halfAngle) * (contentW / window.innerHeight);
  const xFar = visibleHalfW * 0.5;
  const isMobile = window.innerWidth < 600;

  gsap.set(plane.rotation, { y: tau * -0.25 + yOff });
  gsap.set(plane.position, { x: xFar, y: isMobile ? -70 : -32, z: -60 });

  scene.render();

  const sectionDuration = 1;
  const shortDuration = 80 / 140;

  // Parallax
  gsap.to('.airplane-ground', {
    y: '30%',
    scrollTrigger: { trigger: '.airplane-ground-container', scrub: true, start: 'top bottom', end: 'bottom top' },
  });
  gsap.from('.airplane-clouds-overlay', {
    y: '15%',
    scrollTrigger: { trigger: '.airplane-ground-container', scrub: true, start: 'top bottom', end: 'bottom top' },
  });
  gsap.from('.airplane-clouds', {
    y: '25%',
    scrollTrigger: { trigger: '.airplane-ground-container', scrub: true, start: 'top bottom', end: 'bottom top' },
  });

  const tl = gsap.timeline({
    onUpdate: scene.render,
    scrollTrigger: { trigger: '.airplane-content', scrub: true, start: 'top top', end: 'bottom bottom' },
    defaults: { duration: sectionDuration, ease: 'power2.inOut' },
  });

  let delay = 0;

  // 1. Hero — plane slides in smoothly from the right
  tl.to('.airplane-scroll-cta', { duration: 0.25, opacity: 0 }, delay);
  tl.to('canvas', { duration: sectionDuration * 0.6, x: '0%', autoAlpha: 1, ease: 'power1.out' }, delay + sectionDuration * 0.3);

  delay += sectionDuration;

  // 2. Preflight — plane crosses to far left
  tl.to(plane.rotation, { duration: sectionDuration, x: tau * 0.15, y: 0 + yOff, z: -tau * 0.05, ease: 'power1.inOut' }, delay);
  tl.to(plane.position, { duration: sectionDuration, x: -xFar, y: 0, z: -50, ease: 'power1.inOut' }, delay);

  delay += sectionDuration;

  // 3. Takeoff (text RIGHT) → plane stays far left
  tl.to(plane.rotation, { duration: sectionDuration, x: tau * 0.25, y: 0 + yOff, z: -tau * 0.03, ease: 'power1.inOut' }, delay);
  tl.to(plane.position, { duration: sectionDuration, x: -xFar, y: 5, z: -40, ease: 'power1.inOut' }, delay);

  delay += sectionDuration;

  // 3b. Hold left position
  tl.to(plane.rotation, { duration: 0.5, x: tau * 0.25, y: 0 + yOff, z: -tau * 0.03, ease: 'none' }, delay);
  tl.to(plane.position, { duration: 0.5, x: -xFar, y: 5, z: -35, ease: 'power1.inOut' }, delay);

  delay += 0.5;

  // 4. Cruise (text LEFT) → plane crosses to far right
  tl.to(plane.rotation, { duration: sectionDuration, x: tau * 0.2, y: 0 + yOff, z: tau * 0.05, ease: 'power1.inOut' }, delay);
  tl.to(plane.position, { duration: sectionDuration, x: xFar, y: 0, z: -20, ease: 'power1.inOut' }, delay);

  delay += sectionDuration;

  // 4b. Hold cruise position, begin easing into next move
  tl.to(plane.rotation, { duration: 0.3, x: tau * 0.15, y: tau * 0.05 + yOff, z: -tau * 0.02, ease: 'power1.inOut' }, delay);
  tl.to(plane.position, { duration: 0.3, x: xFar * 0.7, y: -2, z: -10, ease: 'power1.inOut' }, delay);

  delay += 0.3;

  // 5. Emergencies (text RIGHT) → plane crosses to far left, slight bank
  tl.to(plane.rotation, { duration: sectionDuration, x: tau * 0.1, y: tau * 0.1 + yOff, z: -tau * 0.08, ease: 'power2.inOut' }, delay);
  tl.to(plane.position, { duration: sectionDuration, x: -xFar, y: -10, z: 20, ease: 'power2.inOut' }, delay);

  delay += sectionDuration;

  // 6. Shutdown (text RIGHT) → plane crosses to far right, levels out
  tl.to(plane.rotation, { duration: sectionDuration, x: 0, y: tau * 0.25 + yOff, z: 0, ease: 'power1.inOut' }, delay);
  tl.to(plane.position, { duration: sectionDuration, x: xFar * 0.6, y: -10, z: 30, ease: 'power1.inOut' }, delay);

  delay += sectionDuration;

  // 7. Hangar (text LEFT) → plane starts rotating right
  tl.to(plane.rotation, { duration: shortDuration, x: 0, y: tau * 0.375 + yOff, z: 0, ease: 'power1.inOut' }, delay);
  tl.to(plane.position, { duration: shortDuration, x: xFar * 0.4, y: -10, z: 30, ease: 'power1.inOut' }, delay);

  delay += shortDuration;

  // 8. Sunset — plane pulls back and rotates
  tl.to(plane.rotation, { duration: shortDuration, x: tau * 0.15, y: tau * 0.85 + yOff, z: 0, ease: 'power1.inOut' }, delay);
  tl.to(plane.position, { duration: shortDuration, z: -100, x: 0, y: 0, ease: 'power1.inOut' }, delay);

  delay += shortDuration;

  // 9. Sunset CTA → plane swoops toward viewer and flies past
  tl.to(plane.rotation, { duration: shortDuration, x: -tau * 0.05, y: tau + yOff, z: -tau * 0.1, ease: 'power1.inOut' }, delay);
  tl.to(plane.position, { duration: shortDuration, x: 0, y: 30, z: 400, ease: 'power2.in' }, delay);
  tl.to(scene.light.position, { duration: shortDuration, x: 0, y: 0, z: 0 }, delay);
}
